import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/use-debounce";
import type { SkillTemplateKey } from "./skillTemplates";

const SLUG_RX = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;

export interface SkillFormValidationInput {
  name: string;
  slug: string;
  description: string;
  type: SkillTemplateKey | "";
  intent_primary: string;
  patterns_pt_br: string[];
  patterns_en: string[];
  prompt_template: string;
  isEdit: boolean;
  currentId?: string | null;
}

export interface SkillFormErrors {
  name?: string;
  slug?: string;
  description?: string;
  type?: string;
  intent_or_patterns?: string;
  prompt?: string;
}

export function useSkillFormValidation(input: SkillFormValidationInput) {
  const [errors, setErrors] = useState<SkillFormErrors>({});
  const [slugChecking, setSlugChecking] = useState(false);
  const debouncedSlug = useDebounce(input.slug, 400);

  // Sync validation
  useEffect(() => {
    const e: SkillFormErrors = {};

    if (!input.name.trim()) {
      e.name = "Nome é obrigatório (mínimo 3 caracteres).";
    } else if (input.name.trim().length < 3) {
      e.name = "Nome muito curto (mínimo 3 caracteres).";
    }

    if (!input.slug.trim()) {
      e.slug = "Slug é obrigatório.";
    } else if (!SLUG_RX.test(input.slug)) {
      e.slug = "Slug deve ser kebab-case (ex: resumir-conversa), 3-64 chars, começa com letra.";
    }

    if (!input.description.trim()) {
      e.description = "Descrição é obrigatória.";
    } else if (input.description.trim().length < 10) {
      e.description = "Descrição muito curta (mínimo 10 caracteres).";
    }

    if (!input.type) {
      e.type = "Selecione um tipo de skill.";
    }

    const hasIntent = input.intent_primary.trim().length > 0;
    const hasPatterns = (input.patterns_pt_br?.length ?? 0) + (input.patterns_en?.length ?? 0) > 0;
    if (!hasIntent && !hasPatterns) {
      e.intent_or_patterns = "Defina ao menos uma intent primária ou um padrão de gatilho.";
    }

    if (input.type === "custom") {
      if (!input.prompt_template.trim()) {
        e.prompt = "Prompt template é obrigatório para skills do tipo Custom.";
      } else if (input.prompt_template.trim().length < 20) {
        e.prompt = "Prompt template muito curto (mínimo 20 caracteres).";
      }
    }

    setErrors((prev) => ({ ...prev, ...e, ...(e.slug ? {} : { slug: prev.slug && prev.slug.startsWith("Slug '") ? prev.slug : undefined }) }));
    // Reset fields not in current sync errors
    setErrors((prev) => {
      const merged: SkillFormErrors = { ...e };
      // Preserve async slug-uniqueness error if format is valid
      if (!e.slug && prev.slug && prev.slug.includes("já existe")) {
        merged.slug = prev.slug;
      }
      return merged;
    });
  }, [
    input.name,
    input.slug,
    input.description,
    input.type,
    input.intent_primary,
    input.patterns_pt_br,
    input.patterns_en,
    input.prompt_template,
  ]);

  // Async slug uniqueness
  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!debouncedSlug || !SLUG_RX.test(debouncedSlug)) return;
      setSlugChecking(true);
      try {
        const { data, error } = await supabase
          .from("syra_skills_v19")
          .select("id")
          .eq("slug", debouncedSlug)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (!error && data && (!input.isEdit || data.id !== input.currentId)) {
          setErrors((prev) => ({ ...prev, slug: `Slug '${debouncedSlug}' já existe. Escolha outro.` }));
        } else {
          setErrors((prev) => {
            if (prev.slug && prev.slug.includes("já existe")) {
              const { slug, ...rest } = prev;
              return rest;
            }
            return prev;
          });
        }
      } finally {
        if (!cancelled) setSlugChecking(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [debouncedSlug, input.isEdit, input.currentId]);

  const hasErrors = Object.values(errors).some((v) => !!v);

  return { errors, hasErrors, slugChecking };
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
